import { ERewardCalendarFormState } from 'constants/enums';
import { collectNestedFormErrorPaths } from 'utils/reactHookFormValidation.utils';

export type RewardCalendarFieldLocation = {
  tab: ERewardCalendarFormState;
  scrollTarget?: string;
};

interface FieldMeta {
  toastDescription: string;
}

const FIELD_META: Record<string, FieldMeta> = {
  name: { toastDescription: 'Please enter a name to continue' },
  publisherOfferId: {
    toastDescription: 'Please enter an external ID to continue'
  },
  offerUiId: { toastDescription: 'Please select an offer design to continue' },
  priority: { toastDescription: 'Please set a priority to continue' }
};

const FIELD_LOCATION_MAP: Record<string, RewardCalendarFieldLocation> = {
  name: {
    tab: ERewardCalendarFormState.GENERAL,
    scrollTarget: 'name'
  },
  publisherOfferId: {
    tab: ERewardCalendarFormState.GENERAL,
    scrollTarget: 'publisherOfferId'
  },
  offerUiId: {
    tab: ERewardCalendarFormState.GENERAL,
    scrollTarget: 'offerUiId'
  },
  priority: {
    tab: ERewardCalendarFormState.GENERAL,
    scrollTarget: 'priority'
  }
};

function getFieldMeta(fieldPath: string): FieldMeta {
  if (FIELD_META[fieldPath]) return FIELD_META[fieldPath];

  const fallbackKey = fieldPath.split('.').pop() ?? fieldPath;
  const fallbackName = fallbackKey
    .replace(/([A-Z])/g, ' $1')
    .trim()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
  return {
    toastDescription: `Please fill in the ${fallbackName.toLowerCase()} to continue`
  };
}

export function getFieldLocation(
  fieldPath: string
): RewardCalendarFieldLocation | undefined {
  if (FIELD_LOCATION_MAP[fieldPath]) return FIELD_LOCATION_MAP[fieldPath];
  for (const [mapPath, location] of Object.entries(FIELD_LOCATION_MAP)) {
    if (fieldPath.startsWith(mapPath)) return location;
  }
  return undefined;
}

export interface RewardCalendarValidationResult {
  firstFieldLocation: RewardCalendarFieldLocation | undefined;
  toastMessage: string;
  toastDescriptions: string[];
}

export function buildRewardCalendarValidationResult(
  errors: Record<string, unknown>
): RewardCalendarValidationResult {
  const missingFieldPaths = collectNestedFormErrorPaths(errors);
  const firstFieldLocation =
    missingFieldPaths.length > 0
      ? getFieldLocation(missingFieldPaths[0])
      : undefined;

  return {
    firstFieldLocation,
    toastMessage: 'Please complete all required fields before saving',
    toastDescriptions: missingFieldPaths.map(
      (p) => getFieldMeta(p).toastDescription
    )
  };
}
